import React, { useEffect, useRef } from 'react'

interface IPulse {
  startX: number
  startY: number
  targetX: number
  targetY: number
  progress: number
  speed: number
  length: number
  idleDelay: number
  burstProgress: number
}

interface IBurst {
  x: number
  y: number
  radius: number
  opacity: number
}

const GRID_SIZE = 120
const OFFSET_X = 60
const OFFSET_Y = 60

export const GridPulseCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || typeof window === 'undefined') return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationFrameId: number

    const resize = () => {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // Active pulses and dot bursts
    const pulses: IPulse[] = []
    const bursts: IBurst[] = []

    const spawnPulse = (initialDelay = 0): IPulse => {
      const cols = Math.max(2, Math.floor(canvas.width / GRID_SIZE))
      const rows = Math.max(2, Math.floor(canvas.height / GRID_SIZE))

      const col = Math.floor(Math.random() * cols)
      const row = Math.floor(Math.random() * rows)

      const startX = col * GRID_SIZE + OFFSET_X
      const startY = row * GRID_SIZE + OFFSET_Y

      const isHorizontal = Math.random() > 0.5
      const direction = Math.random() > 0.5 ? 1 : -1
      const steps = 1 // Travel exactly 1 grid step from point A to point B

      const targetX = isHorizontal ? startX + direction * steps * GRID_SIZE : startX
      const targetY = isHorizontal ? startY : startY + direction * steps * GRID_SIZE

      return {
        startX,
        startY,
        targetX,
        targetY,
        progress: 0,
        speed: 0.004 + Math.random() * 0.003, // Graceful, slow electric travel
        length: 50 + Math.random() * 20,
        idleDelay: initialDelay,
        burstProgress: 0,
      }
    }

    // Moderate, subtle count: 3-4 gentle pulses at any given moment
    for (let i = 0; i < 4; i++) {
      pulses.push(spawnPulse(i * 90 + Math.floor(Math.random() * 120)))
    }

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // 1. Render & update expanding bursts at destination dots
      for (let i = bursts.length - 1; i >= 0; i--) {
        const b = bursts[i]
        b.radius += 0.45
        b.opacity -= 0.025

        if (b.opacity <= 0) {
          bursts.splice(i, 1)
          continue
        }

        // Soft white ring flare
        ctx.beginPath()
        ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2)
        ctx.strokeStyle = `rgba(255, 255, 255, ${b.opacity * 0.85})`
        ctx.lineWidth = 1.5
        ctx.shadowColor = '#FFFFFF'
        ctx.shadowBlur = 10
        ctx.stroke()

        // Glowing core dot
        ctx.beginPath()
        ctx.arc(b.x, b.y, 3.5, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 255, 255, ${b.opacity})`
        ctx.shadowColor = '#FFFFFF'
        ctx.shadowBlur = 16
        ctx.fill()
      }

      // 2. Render & update traveling lines
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i]

        if (p.idleDelay > 0) {
          p.idleDelay--
          continue
        }

        p.progress += p.speed

        if (p.progress >= 1) {
          // Trigger flare/burst on arrival at target point
          bursts.push({
            x: p.targetX,
            y: p.targetY,
            radius: 3,
            opacity: 1,
          })

          // Respawn with a calm pause before the next pulse starts
          pulses[i] = spawnPulse(100 + Math.floor(Math.random() * 200))
          continue
        }

        const currentX = p.startX + (p.targetX - p.startX) * p.progress
        const currentY = p.startY + (p.targetY - p.startY) * p.progress

        const dx = p.targetX - p.startX
        const dy = p.targetY - p.startY
        const dist = Math.sqrt(dx * dx + dy * dy) || 1
        const tailLength = Math.min(p.length, dist * p.progress)

        const tailX = currentX - (dx / dist) * tailLength
        const tailY = currentY - (dy / dist) * tailLength

        // Pure white glowing beam
        const grad = ctx.createLinearGradient(tailX, tailY, currentX, currentY)
        grad.addColorStop(0, 'rgba(255, 255, 255, 0)')
        grad.addColorStop(0.6, 'rgba(255, 255, 255, 0.75)')
        grad.addColorStop(1, 'rgba(255, 255, 255, 1)')

        ctx.beginPath()
        ctx.moveTo(tailX, tailY)
        ctx.lineTo(currentX, currentY)
        ctx.strokeStyle = grad
        ctx.lineWidth = 2.0
        ctx.lineCap = 'round'
        ctx.shadowColor = '#FFFFFF'
        ctx.shadowBlur = 8
        ctx.stroke()

        // Traveling head dot
        ctx.beginPath()
        ctx.arc(currentX, currentY, 2.2, 0, Math.PI * 2)
        ctx.fillStyle = '#FFFFFF'
        ctx.shadowColor = '#FFFFFF'
        ctx.shadowBlur = 10
        ctx.fill()
      }

      animationFrameId = requestAnimationFrame(render)
    }

    render()

    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-10 opacity-90"
    />
  )
}
